import { FileDoneOutlined } from "@ant-design/icons";
import { SchoolCourse } from "@axl-dev/school-sdk";
import { Progress, Tooltip } from "antd";
import { useMemo, useRef } from "react";

import { useFileProvider, useSdk } from "@app/shared/lib/hooks";
import { saveAs } from "@app/shared/lib/save-as";
import {
  GradientPlaceholder,
  Image,
  Loc,
  ValuePlaceholder,
} from "@app/shared/ui";
import Request from "@app/shared/ui/Request";

import { NavigateButton } from "./components/NavigateButton/NavigateButton";

type Props = {
  course: SchoolCourse;
  /** Override title position: "onImage" (default) or "belowImage" */
  titlePosition?: "onImage" | "belowImage";
  /** Override button labels */
  buttonLabels?: { continue?: string; start?: string };
};

export const CourseCard: React.FC<Props> = ({ course, titlePosition, buttonLabels }) => {
  const fileProvider = useFileProvider();
  const sdk = useSdk();
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const canDownloadCert = useMemo(() => course.certId, [course.certId]);
  const isBelowImage = titlePosition === "belowImage";

  const titleBlock = (
    <div
      className="material-card-title-wrapper"
      title={course.title}
      data-testid="course-title"
    >
      <ValuePlaceholder
        value={course.title}
        className="material-card-title"
      />
    </div>
  );

  return (
    <div
      className="shadow-sm material-card"
      data-testid="course-card"
      data-id={course.id}
      onClick={() => buttonRef.current?.click()}
    >
      <div className="material-card-image" data-testid="course-preview">
        <Image
          src={fileProvider.getUrlByKey(course.primaryImageCloudKey)}
          fit="cover"
          width="100%"
          height="100%"
          placeholder={
            <GradientPlaceholder
              uniq={course.id}
              width="100%"
              height="100%"
              className="material-card-image-placeholder"
            />
          }
        />

        {!isBelowImage && titleBlock}

        {/* Certificate download badge — pinned to the top-right corner of
            the cover image so it doesn't displace the progress bar below.
            Works identically on desktop and mobile. */}
        {canDownloadCert && (
          <Request
            action={() => sdk.api.file.download(course.certId!)}
            onSuccess={(url) => saveAs(url)}
          >
            {({ exec }) => (
              <Tooltip title={<Loc word="Cert.download" default="Download certificate" />}>
                <button
                  type="button"
                  className="material-card-cert-badge"
                  data-testid="cert-download-badge"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    exec();
                  }}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: "rgba(255,255,255,0.92)",
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    fontSize: 18,
                    padding: 0,
                    zIndex: 2,
                  }}
                >
                  <FileDoneOutlined />
                </button>
              </Tooltip>
            )}
          </Request>
        )}
      </div>

      <div className="material-card-content">
        {isBelowImage && titleBlock}
        <div
          className="material-card-progress-current-name"
          data-testid="course-current-step-title"
        >
          <ValuePlaceholder
            value={course.studentCourse?.currentCourseItem?.title}
            placeholder={
              Math.round(course.studentCourse?.progress ?? 0) >= 100
                ? <Loc word="Course.completedLabel" default="Completed" />
                : course.studentCourse?.progress != null && course.studentCourse.progress > 0
                  ? <Loc word="Course.inProgressLabel" default="In progress" />
                  : <Loc word="Course.noCurrentItemLabel" default="Not started" />
            }
          />
        </div>
        <div
          className="material-card-progress flex align-center gap-10"
          data-testid="course-progress"
          data-progress={Math.round(course.studentCourse?.progress ?? 0)}
        >
          <Progress
            percent={Math.round(course.studentCourse?.progress ?? 0)}
            size="small"
            status="active"
          />
        </div>

        <div className="flex flex-col mb-10" data-testid="course-desc">
          <Loc
            word="Course.descLabel"
            default="Description"
            className="fw-600 secondary-text"
          />
          <div className="material-card-desc">
            <ValuePlaceholder
              value={course.shortDescription}
              placeholder="-"
            />
          </div>
        </div>

        <NavigateButton buttonRef={buttonRef} course={course} buttonLabels={buttonLabels} />
      </div>
    </div>
  );
};
